import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const FORM_URL = "https://readdy.ai/api/form/da1d9otrlbpoffc5h4j0";

const VALUES = [
  {
    icon: "ri-stack-line",
    title: "番剧库全收录",
    desc: "动画、剧场版、OVA、泡面番与国漫全类型一网打尽，新番与经典同步更新。",
  },
  {
    icon: "ri-flashlight-line",
    title: "点开即看",
    desc: "无需等待与繁琐操作，点开即看全集，为追番节省每一分钟。",
  },
  {
    icon: "ri-team-line",
    title: "找番更方便",
    desc: "清晰分类与高效搜索，帮你快速锁定想看的番剧，不错过任何好作品。",
  },
];

const PRINCIPLES = [
  "内容优先：始终以优质番剧与顺畅观感为核心。",
  "同步更新：剧场版与OVA第一时间跟进，满足追更需求。",
  "分类清晰：全类型归整，找番一目了然。",
  "干净体验：界面清爽，专注看番本身。",
];

const QUOTES = [
  { text: "樱花看番的番剧库太全了，想看的剧场版和OVA都能找到，追番体验非常舒服。", name: "追番十年的小K" },
  { text: "泡面番专区是我的心头好，三分钟一集，每天午休刚好看完两集，很解压。", name: "午休追番的Momo" },
  { text: "国漫专区更新很快，长安墨客这类水墨质感的作品在这里看得很过瘾。", name: "国漫爱好者阿岚" },
];

const FAQS = [
  { q: "樱花看番收录哪些类型的番剧？", a: "樱花看番番剧库全收录，涵盖动画番剧、剧场版、OVA特别篇、泡面番以及国漫全类型，并在持续扩充。", },
  { q: "番剧内容多久更新一次？", a: "热门连载番与剧场版同步更新，OVA与国漫每周持续更新，收藏后更新会在首页优先推荐。", },
  { q: "如何快速找到想看的番？", a: "可通过番剧库的类型筛选，或使用顶部搜索框输入番名、题材关键词，即可快速定位。", },
  { q: "是否需要注册才能看番？", a: "不需要。樱花看番坚持点开即看，打开即可观看全集，无需繁琐注册流程。", },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    document.title = "关于我们 - 樱花看番";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "关于樱花看番：番剧库全收录，涵盖动画、剧场版、OVA、泡面番与国漫。了解我们的定位、内容原则与用户口碑，并联系我们。"
      );
    }
  }, []);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const aux = (fd.get("phone_alt") as string)?.trim();
    if (aux) {
      setFormStatus({ ok: true, msg: "感谢你的反馈，我们会尽快与你联系。" });
      return;
    }

    fd.delete("phone_alt");
    setSubmitting(true);
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        body: fd,
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const isSpam =
        !res.ok ||
        !parsed ||
        parsed.code !== "OK" ||
        String(serverMsg).toLowerCase().includes("spam");

      if (isSpam) {
        setFormStatus(null);
        setFormError(serverMsg || "提交失败，请稍后再试");
        return;
      }
      setFormStatus({ ok: true, msg: serverMsg || "提交成功，感谢你的反馈！" });
      form.reset();
    } catch (err) {
      setFormStatus(null);
      setFormError("网络异常，请稍后再试");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-background-200/60 bg-background-100 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-600 outline-none transition-colors focus:border-primary-500/60";

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* header */}
        <section className="relative flex h-64 items-end overflow-hidden md:h-80">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20anime%20style%20illustration%20of%20an%20open%20book%20with%20glowing%20cherry%20blossoms%20and%20film%20reel%20flowing%20out%2C%20cozy%20light%2C%20sakura%20theme%2C%20stylized%20background&width=1600&height=640&seq=about-sakura-book&orientation=landscape"
            alt="樱花看番 关于我们 背景"
            title="关于樱花看番"
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-950/50 to-background-950/30" />
          <div className="relative z-10 mx-auto w-full max-w-7xl px-4 md:px-6 pb-8">
            <nav className="mb-3 flex items-center gap-1 text-xs text-foreground-600">
              <Link to="/" className="hover:text-primary-300">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-400">关于我们</span>
            </nav>
            <h1 className="font-heading text-3xl text-foreground-950 md:text-5xl">关于樱花看番</h1>
            <p className="mt-2 max-w-xl text-sm text-foreground-600 md:text-base">
              一个专注让找番更简单的番剧库，为热爱动漫的你而生。
            </p>
          </div>
        </section>

        {/* story */}
        <section className="mx-auto max-w-7xl px-4 md:px-6 py-14">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="mb-2 text-xs tracking-wide text-primary-400">品牌故事</p>
              <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">
                把散落各处的番，收进一个樱花屋
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-7 text-foreground-700">
                <p>
                  樱花看番诞生于一群动漫爱好者的共同愿望：让每一个想找番的人，都能在最快的时间、用最简单的方式，看到想看的动画、剧场版、OVA、泡面番与国漫。
                </p>
                <p>
                  我们相信，好的观番体验不在于复杂的入口，而在于「点开即看」的顺畅与「全类型收录」的安心。于是有了樱花看番番剧库，把全类型的优秀作品整齐收纳，等你随时来取。
                </p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-primary-800/40 bg-primary-500/10 p-6">
                <p className="font-heading text-3xl text-primary-300">12000+</p>
                <p className="mt-2 text-sm text-foreground-700">全类型番剧收录</p>
              </div>
              <div className="rounded-xl border border-accent-500/30 bg-accent-500/10 p-6">
                <p className="font-heading text-3xl text-accent-300">7×24</p>
                <p className="mt-2 text-sm text-foreground-700">新番与剧场版同步更新</p>
              </div>
            </div>
          </div>
        </section>

        {/* values */}
        <section className="bg-background-100/60 py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <p className="mb-2 text-xs tracking-wide text-primary-400">我们的定位</p>
            <h2 className="mb-8 font-heading text-2xl text-foreground-950 md:text-3xl">
              让看番这件事，简单而美好
            </h2>
            <div className="grid gap-4 md:grid-cols-3">
              {VALUES.map((v) => (
                <div
                  key={v.title}
                  className="rounded-xl border border-background-200/60 bg-background-100 p-6"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
                    <i className={`${v.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 font-heading text-lg text-foreground-950">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* principles */}
        <section className="mx-auto max-w-7xl px-4 md:px-6 py-14">
          <div className="rounded-2xl border border-background-200/60 bg-background-100 p-8 md:p-12">
            <h2 className="mb-6 font-heading text-2xl text-foreground-950 md:text-3xl">
              内容原则
            </h2>
            <ul className="grid gap-4 md:grid-cols-2">
              {PRINCIPLES.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-500/20 text-accent-300">
                    <i className="ri-check-line" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* reputation */}
        <section className="bg-background-100/60 py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <p className="mb-2 text-xs tracking-wide text-accent-400">用户口碑</p>
            <h2 className="mb-8 font-heading text-2xl text-foreground-950 md:text-3xl">
              追番人怎么说
            </h2>
            <div className="grid gap-4 md:grid-cols-3">
              {QUOTES.map((q) => (
                <figure
                  key={q.name}
                  className="rounded-xl border border-background-200/60 bg-background-100 p-6"
                >
                  <i className="ri-double-quotes-l text-2xl text-primary-400" />
                  <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">
                    {q.text}
                  </blockquote>
                  <figcaption className="mt-4 text-xs text-foreground-500">— {q.name}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* faq */}
        <section className="mx-auto max-w-7xl px-4 md:px-6 py-14">
          <h2 className="mb-6 font-heading text-2xl text-foreground-950 md:text-3xl">
            常见问题
          </h2>
          <div className="max-w-3xl space-y-3">
            {FAQS.map((f, i) => (
              <div
                key={f.q}
                className="overflow-hidden rounded-xl border border-background-200/60 bg-background-100"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-medium text-foreground-950">{f.q}</span>
                  <i
                    className={`ri-add-line text-foreground-500 transition-transform duration-300 ${
                      openFaq === i ? "rotate-45" : ""
                    }`}
                  />
                </button>
                <div
                  className={`grid transition-all duration-300 ${
                    openFaq === i ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600">{f.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* contact */}
        <section className="bg-background-100/60 py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
                <p className="mb-2 text-xs tracking-wide text-primary-400">联系我们</p>
                <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">
                  有想看的番，或想说的话？
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  欢迎通过右侧表单告诉我们你的建议、希望收录的番剧或遇到的问题，我们会认真阅读每一条反馈。
                </p>
                <div className="mt-6 space-y-3 text-sm text-foreground-700">
                  <p className="flex items-center gap-2">
                    <i className="ri-mail-line text-primary-400" /> contact@sakura-kankan.example.com
                  </p>
                  <p className="flex items-center gap-2">
                    <i className="ri-wechat-line text-primary-400" /> 樱花看番官方公众号
                  </p>
                  <p className="flex items-center gap-2">
                    <i className="ri-time-line text-primary-400" /> 工作日 10:00 - 18:00
                  </p>
                </div>
              </div>

              <form
                id="sakura-contact-form"
                data-readdy-form="樱花看番联系表单"
                onSubmit={onSubmit}
                className="rounded-xl border border-background-200/60 bg-background-50 p-6"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-xs text-foreground-600">称呼</label>
                    <input id="name" name="name" type="text" required maxLength={30} placeholder="怎么称呼你" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-xs text-foreground-600">邮箱</label>
                    <input id="email" name="email" type="email" required placeholder="you@example.com" className={inputCls} />
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="topic" className="mb-1.5 block text-xs text-foreground-600">反馈类型</label>
                  <select id="topic" name="topic" className={inputCls} defaultValue="希望收录的番剧">
                    <option>希望收录的番剧</option>
                    <option>观看建议与反馈</option>
                    <option>合作与版权</option>
                    <option>其他</option>
                  </select>
                </div>
                <div className="mt-4">
                  <label htmlFor="message" className="mb-1.5 block text-xs text-foreground-600">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请简要描述你的需求或建议（500字以内）"
                    className={inputCls}
                  />
                </div>
                {/* anti-spam auxiliary field */}
                <input
                  type="text"
                  name="phone_alt"
                  className="form-aux"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div className="mt-5">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400 disabled:opacity-60"
                  >
                    <i className="ri-send-plane-line" />
                    {submitting ? "提交中…" : "提交反馈"}
                  </button>
                  {formStatus?.ok && (
                    <p className="mt-3 rounded-lg bg-accent-500/10 px-4 py-3 text-sm text-accent-300">
                      {formStatus.msg}
                    </p>
                  )}
                  {formError && (
                    <p className="mt-3 rounded-lg bg-primary-500/10 px-4 py-3 text-sm text-primary-300">
                      {formError}
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}